import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { createTwoDecimal } from '@models/editor';

@Component({
    selector: 'app-editor-input-text',
    templateUrl: './editor-input-text.component.html',
    styleUrls: ['./editor-input-text.component.css'],
    standalone: false,
})
export class EditorInputTextComponent implements OnInit, OnChanges {
    @Input() name: string;
    @Input() label: string;
    @Input() value: string;
    @Input() frontIcon: string;
    @Input() frontIconRotate: string = '0deg';
    @Input() endIcon: string;
    @Input() unit: string;
    @Input() type: string = 'number';
    @Input() readonly: boolean;
    @Input() maxlength: number = 20;
    @Input() focusEffect: boolean = false;
    @Input() shouldEmitOnBlur: boolean = false;
    @Input() showTwoDecimal: boolean = false;
    @Input() minValue: number;
    @Input() maxValue: number;
    @Input() allowDecimal: boolean = true;

    @Input() light: boolean = false;

    @Output() onIconClick = new EventEmitter();
    @Output() onInputChange = new EventEmitter();
    @Output() onInputKeyDown = new EventEmitter();

    @ViewChild('input') input: ElementRef;
    @ViewChild('inputContainer') inputContainer: ElementRef;

    inputValue: string = '';
    focus: boolean = false;

    shouldIgnoreBlur: boolean = false;

    @HostListener('document:click', ['$event'])
    handleClickOut(event: MouseEvent) {
        if (this.focus && !this.inputContainer.nativeElement.contains(event.target)) {
            this.focus = false;
            this.input.nativeElement.value = this.value;
            this.shouldIgnoreBlur = true;
            this.input.nativeElement.blur();
        }
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent) {
        if (this.shouldEmitOnBlur && this.focus) {
            this.inputValue = Number(this.inputValue).toString();
            if (this.unit === '%') {
                if (Number(this.inputValue) > 100) {
                    this.input.nativeElement.value = '100';
                    this.inputValue = '100';
                } else if (Number(this.inputValue) < 0) {
                    this.input.nativeElement.value = '0';
                    this.inputValue = '0';
                }
            }
            if ((this.minValue || this.minValue == 0) && Number(this.inputValue) < this.minValue) {
                this.inputValue = this.minValue.toString();
                this.input.nativeElement.value = this.minValue.toString();
            }
            if ((this.maxValue || this.maxValue == 0) && Number(this.inputValue) > this.maxValue) {
                this.inputValue = this.maxValue.toString();
                this.input.nativeElement.value = this.maxValue.toString();
            }
            this.onInputChange.emit({ name: this.name, value: this.inputValue });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value) {
            if (this.showTwoDecimal) {
                const twoDecimalNumber = createTwoDecimal(this.value);
                if (!isNaN(Number(twoDecimalNumber))) this.inputValue = twoDecimalNumber;
                else this.inputValue = this.value;
            } else {
                if (this.type === 'number') {
                    this.inputValue = Number(this.value).toFixed(0);
                } else {
                    this.inputValue = this.value;
                }
            }
        }
    }

    ngOnInit() {
        this.inputValue = this.value !== undefined ? this.value : undefined;
        if (this.value === undefined || this.value === null || this.value === '') {
            this.inputValue = '';
            return;
        }
        if (this.showTwoDecimal) {
            const twoDecimalNumber = createTwoDecimal(this.inputValue);
            if (!isNaN(Number(twoDecimalNumber))) this.inputValue = twoDecimalNumber;
        } else {
            if (this.type === 'number') {
                this.inputValue = Number(this.value).toFixed(0);
            } else {
                this.inputValue = this.value;
            }
        }
    }

    inputFocus() {
        if (this.readonly) return;
        this.focus = true;
    }

    inputBlur() {
        if (this.readonly) return;
        if (this.shouldIgnoreBlur) {
            this.shouldIgnoreBlur = false;
            return;
        }
        if (this.shouldEmitOnBlur && this.focus) {
            this.inputValue = Number(this.inputValue).toString();
            if (this.unit === '%') {
                if (Number(this.inputValue) > 100) {
                    this.input.nativeElement.value = '100';
                    this.inputValue = '100';
                } else if (Number(this.inputValue) < 0 || !this.inputValue) {
                    this.input.nativeElement.value = '0';
                    this.inputValue = '0';
                }
            }
            if ((this.minValue || this.minValue == 0) && Number(this.inputValue) < this.minValue) {
                this.inputValue = this.minValue.toString();
                this.input.nativeElement.value = this.minValue.toString();
            }
            if ((this.maxValue || this.maxValue == 0) && Number(this.inputValue) > this.maxValue) {
                this.inputValue = this.maxValue.toString();
                this.input.nativeElement.value = this.maxValue.toString();
            }
            this.onInputChange.emit({ name: this.name, value: this.inputValue });
        }
        this.focus = false;
    }

    clickIcon() {
        if (!this.readonly) this.onIconClick.emit();
    }

    onChange(value: string) {
        this.shouldIgnoreBlur = false;
        if (this.readonly || this.shouldEmitOnBlur) return;
        if (!!this.allowDecimal && value.includes('.')) value = value.replace(/\./g, '');
        if ((this.minValue || this.minValue == 0) && Number(value) < this.minValue) {
            value = this.minValue.toString();
            this.input.nativeElement.value = this.minValue.toString();
        }
        if ((this.maxValue || this.maxValue == 0) && Number(value) > this.maxValue) {
            value = this.maxValue.toString();
            this.input.nativeElement.value = this.maxValue.toString();
        }
        this.onInputChange.emit({ name: this.name, value: value });
    }

    inputKeyDown(event) {
        this.onInputKeyDown?.emit({ event, value: this.inputValue });
    }
}
